import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { formatPrice, FREE_SHIPPING_THRESHOLD, resolveImg } from "../lib/api";
import StockNotes from "./StockNotes";

export default function CartDrawer() {
  const { drawerOpen, setDrawerOpen, items, updateQty, removeItem, subtotal, refreshStock } = useCart();

  useEffect(() => {
    if (drawerOpen) refreshStock();
  }, [drawerOpen, refreshStock]);

  if (!drawerOpen) return null;

  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="cart-drawer">
      <div
        className="absolute inset-0 bg-[var(--ink-forest)]/40 backdrop-blur-sm"
        onClick={() => setDrawerOpen(false)}
      />
      <div className="ml-auto relative h-full w-full sm:w-[440px] bg-[var(--bg-surface)] shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="px-6 py-5 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[var(--ink-forest)]">Your Basket</h2>
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-2 rounded-full hover:bg-[var(--muted)]"
            data-testid="cart-close-btn"
            aria-label="Close cart"
          >
            <X className="w-5 h-5 text-[var(--ink-forest)]" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <ShoppingBag className="w-16 h-16 text-[var(--sage)] mb-4" />
            <p className="font-serif text-2xl text-[var(--ink-forest)] mb-2">Your basket is empty</p>
            <p className="text-sm text-[var(--muted-fg)] mb-6">
              Discover oils, gifts, and treasures.
            </p>
            <Link
              to="/shop"
              onClick={() => setDrawerOpen(false)}
              className="bg-[var(--terracotta)] text-white rounded-full px-8 py-3 text-sm font-medium hover:bg-[var(--terracotta-deep)] transition-colors"
              data-testid="cart-empty-shop-btn"
            >
              Start shopping
            </Link>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 bg-[var(--muted)]/40">
              {remaining > 0 ? (
                <p className="text-xs text-[var(--ink-forest)] mb-2">
                  Add <span className="font-semibold text-[var(--terracotta)]">{formatPrice(remaining)}</span> more for{" "}
                  <span className="font-semibold">free shipping</span>
                </p>
              ) : (
                <p className="text-xs font-semibold text-[var(--terracotta)] mb-2">
                  ✦ You unlocked free shipping!
                </p>
              )}
              <div className="h-1.5 w-full bg-[var(--border)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--terracotta)] rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                  data-testid="free-shipping-progress"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="cart-items-list">
              <StockNotes />
              {items.map((item) => (
                <div
                  key={item.product_id}
                  className="flex gap-4 pb-4 border-b border-[var(--border)] last:border-0"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <img
                    src={resolveImg(item.image_url)}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-xl border border-[var(--border)] bg-[var(--muted)]"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-base text-[var(--ink-forest)] line-clamp-2">{item.name}</p>
                    <p className="text-xs text-[var(--muted-fg)] mt-0.5">
                      {formatPrice(item.price)}
                      {typeof item.stock === "number" && item.quantity >= item.stock && item.stock <= 5 && (
                        <span className="text-[var(--terracotta)]" data-testid={`drawer-max-${item.product_id}`}> · only {item.stock} available</span>
                      )}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="inline-flex items-center border border-[var(--border)] rounded-full">
                        <button
                          onClick={() => updateQty(item.product_id, item.quantity - 1)}
                          className="p-1.5 hover:bg-[var(--muted)] rounded-full"
                          data-testid={`qty-decrease-${item.product_id}`}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="px-3 text-sm font-medium" data-testid={`qty-value-${item.product_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQty(item.product_id, item.quantity + 1)}
                          disabled={typeof item.stock === "number" && item.quantity >= item.stock}
                          className="p-1.5 hover:bg-[var(--muted)] rounded-full disabled:opacity-30 disabled:hover:bg-transparent"
                          data-testid={`qty-increase-${item.product_id}`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.product_id)}
                        className="text-xs text-[var(--muted-fg)] hover:text-[var(--terracotta)] transition-colors p-1"
                        data-testid={`remove-${item.product_id}`}
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-[var(--ink-forest)] whitespace-nowrap">
                    {formatPrice(item.price * item.quantity)}
                  </p>
                </div>
              ))}
            </div>

            <div className="px-6 py-5 border-t border-[var(--border)] bg-[var(--bg-cream)] space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[var(--muted-fg)]">Subtotal</span>
                <span className="font-semibold text-[var(--ink-forest)]" data-testid="cart-subtotal">
                  {formatPrice(subtotal)}
                </span>
              </div>
              <p className="text-xs text-[var(--muted-fg)]">
                Shipping & taxes calculated at checkout.
              </p>
              <Link
                to="/cart"
                onClick={() => setDrawerOpen(false)}
                className="block w-full text-center bg-[var(--terracotta)] text-white rounded-full py-3.5 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors"
                data-testid="cart-view-btn"
              >
                View basket & checkout
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
